import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import { Plus, Trash, ArrowLeft, GripVertical, Calendar3, CloudArrowUp, ChevronLeft, XCircle } from "react-bootstrap-icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import storeApis from "../../../api/storeApi";
import "./Contests.css";

const CONTEST_TYPES = ["Trip", "Cash", "Gift"];
const FREQUENCIES = [
  { value: "campaign", label: "Campaign (One-time)" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];
const PROGRESS_METRICS = ["Sales Value (Revenue)"];
const REWARD_TYPES = ["Trip/Vacation", "Incentive"];
const CATEGORIES = ["Sales & Performance"];
/** API uses numeric strings; UI shows labels. */
const LEADERBOARD_TYPE_OPTIONS = [
  { value: "1", label: "Overall Ranking" },
  { value: "2", label: "Category-wise" },
  { value: "3", label: "Region-wise" },
];

const mapLeaderboardTypeFromApi = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "1";
  const s = String(raw).trim();
  if (s === "1" || s === "2" || s === "3") return s;
  const n = Number(s);
  if (n === 1 || n === 2 || n === 3) return String(n);
  const lower = s.toLowerCase();
  const byLabel = LEADERBOARD_TYPE_OPTIONS.find((o) => o.label.toLowerCase() === lower);
  if (byLabel) return byLabel.value;
  return "1";
};
const ELIGIBLE_USER_TYPES = [
  { value: "1", label: "All Active Agents" },
  { value: "2", label: "Specific Category" },
];

const defaultMilestone = () => ({
  id: `m-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  /** Set when row was loaded from API; omitted for newly added rows (create / edit). */
  serverMilestoneId: undefined,
  name: "",
  targetPercent: "",
  rewardType: "",
  rewardDetails: "",
  enabled: true,
});

/** DB id from fetch-contest / list item (never use client drag `id` for API). */
const getApiMilestoneId = (m) => {
  const raw = m?.milestone_id ?? m?.milestoneId ?? m?.id;
  if (raw === null || raw === undefined || raw === "") return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  if (s.startsWith("m-")) return undefined;
  return s;
};

/** YYYY-MM-DD in local timezone (for `<input type="date" min=...>`). */
const localDateInputValue = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** True when fetch-contest sends an explicit leaderboard type (1/2/3), including when `leaderboard_link` is 0. */
const isLeaderboardTypePresent = (data) => {
  const ltRaw = data?.leaderboard_type ?? data?.leaderboardType;
  if (ltRaw === undefined || ltRaw === null || String(ltRaw).trim() === "") return false;
  const s = String(ltRaw).trim();
  if (s === "1" || s === "2" || s === "3") return true;
  const n = Number(s);
  if (n === 1 || n === 2 || n === 3) return true;
  const lower = s.toLowerCase();
  return LEADERBOARD_TYPE_OPTIONS.some((o) => o.label.toLowerCase() === lower);
};

/** Enable Leaderboard switch from API — prefers `is_leaderboard`, then `leaderboard_link`, then `leaderboard_type`. */
const mapEnableLeaderboardFromApi = (data) => {
  if (!data || typeof data !== "object") return true;
  const raw = data.is_leaderboard;
  if (raw !== undefined && raw !== null && raw !== "") {
    return raw === 1 || raw === true || String(raw) === "1";
  }
  if (data.leaderboard_link === 1 || data.leaderboard_link === true) return true;
  if (data.enableLeaderboard === true) return true;
  if (isLeaderboardTypePresent(data)) return true;
  return false;
};

const normalizeEligibleUsers = (value) => {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((v) => {
        if (v && typeof v === "object") {
          return v.id ?? v.partner_category_id ?? v.category_id ?? v.value ?? "";
        }
        return v;
      })
      .map((v) => String(v).trim())
      .filter(Boolean);
    return cleaned.length ? Array.from(new Set(cleaned)) : [];
  }

  if (value === null || value === undefined || value === "") {
    return [];
  }

  const str = String(value).trim();
  if (!str) return [];

  const list = str
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  return list.length ? Array.from(new Set(list)) : [];
};

const toApiPartnerCategory = (value) => {
  const n = Number(value);
  return Number.isNaN(n) ? value : n;
};

const CreateContestModal = ({ onClose, onSave, editContest = null, asPage = false, authToken = "" }) => {
  const isEdit = Boolean(editContest?.id);
  const bannerInputRef = useRef(null);
  const bannerSectionRef = useRef(null);
  const contestNameSectionRef = useRef(null);

  const [status, setStatus] = useState(editContest?.status ?? "draft");
  const [contestName, setContestName] = useState(editContest?.name ?? "");
  const [contestNameError, setContestNameError] = useState("");
  const [subtitle, setSubtitle] = useState(editContest?.subtitle ?? "");
  const [description, setDescription] = useState(editContest?.description ?? "");
  const [contestType, setContestType] = useState(editContest?.contestType ?? editContest?.type ?? "Trip");
  const [category, setCategory] = useState(editContest?.category ?? "Sales & Performance");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerDragOver, setBannerDragOver] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(editContest?.banner_image || editContest?.imageUrl || null);
  const [bannerError, setBannerError] = useState("");
  const [startDate, setStartDate] = useState(editContest?.startDate ?? "");
  const [endDate, setEndDate] = useState(editContest?.endDate ?? "");

  const todayStr = localDateInputValue();
  const endDateMin =
    startDate && startDate >= todayStr ? startDate : todayStr;

  const [frequency, setFrequency] = useState(editContest?.frequency ?? "campaign");
  const [resetProgress, setResetProgress] = useState(editContest?.resetProgress ?? false);
  const [progressMetric, setProgressMetric] = useState(editContest?.progressMetric ?? "Sales Value (Revenue)");
  const [targetValue, setTargetValue] = useState(editContest?.targetValue ?? "");
  const [displayLabel, setDisplayLabel] = useState(editContest?.displayLabel ?? "Revenue to go");
  const [minThreshold, setMinThreshold] = useState(editContest?.minThreshold ?? "");
  const [milestones, setMilestones] = useState(() => [defaultMilestone()]);
  const [enableLeaderboard, setEnableLeaderboard] = useState(() => mapEnableLeaderboardFromApi(editContest));
  const [leaderboardType, setLeaderboardType] = useState(() =>
    mapLeaderboardTypeFromApi(editContest?.leaderboard_type ?? editContest?.leaderboardType)
  );
  const [eligibleUserType, setEligibleUserType] = useState(
    String(editContest?.eligible_users ?? editContest?.eligibleUsers ?? "1") === "2" ? "2" : "1"
  );
  const [eligibleUsers, setEligibleUsers] = useState(
    normalizeEligibleUsers(editContest?.partner_category ?? editContest?.partnerCategory ?? [])
  );
  const [eligibleUsersSearch, setEligibleUsersSearch] = useState("");
  const [partnerCategories, setPartnerCategories] = useState([]);

  const eligibilityOptions = partnerCategories;
  const categoryOptions = partnerCategories;
  const areAllCategoriesSelected =
    categoryOptions.length > 0 &&
    categoryOptions.every((opt) => eligibleUsers.includes(String(opt.value)));

  const filteredEligibilityOptions = eligibilityOptions.filter((option) =>
    option.label.toLowerCase().includes(eligibleUsersSearch.trim().toLowerCase())
  );

  // Fetch partner categories for eligibility dropdown.
  useEffect(() => {
    const token = authToken || JSON.parse(localStorage.getItem("authToken") || "null");
    if (!token) return;

    const fetchPartnerCategories = async () => {
      try {
        const response = await storeApis.post(
          "/framework/api/partner-category",
          {
            startlimit: 0,
            endlimit: 20,
            search: "",
            sort: "",
            action: "list",
          },
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const rawList =
          response?.data?.response?.data ||
          response?.data?.response ||
          [];
        if (!Array.isArray(rawList)) {
          setPartnerCategories([]);
          return;
        }
        const mapped = rawList
          .map((item) => {
            const rawValue =
              item?.id ??
              item?.partner_category_id ??
              item?.category_id ??
              item?.partner_category ??
              item?.value;
            const rawLabel =
              item?.partner_category ??
              item?.category_name ??
              item?.name ??
              item?.label;
            if (rawValue === null || rawValue === undefined) return null;
            const value = String(rawValue).trim();
            const label = String(rawLabel || "").trim() || value;
            if (!value) return null;
            return { value, label };
          })
          .filter(Boolean);
        setPartnerCategories(mapped);
      } catch (error) {
        console.error("Failed to load partner categories", error);
        setPartnerCategories([]);
      }
    };

    fetchPartnerCategories();
  }, [authToken]);

  // Map API response fields to form fields when editContest changes
  useEffect(() => {
    console.log("CreateContest useEffect - editContest:", editContest);
    // Only map if we have contest data (check for contest_name OR name to handle both formats)
    if (editContest && editContest.id && (editContest.contest_name || editContest.name)) {
      // Map API snake_case fields to component camelCase fields
      const apiData = editContest;
      console.log("Mapping API data to form fields:", apiData);
      console.log("Milestones in editContest:", apiData.milestones);
      
      // Map frequency from API (one-time -> campaign, monthly -> monthly, quarterly -> quarterly)
      const mapFrequency = (freq) => {
        if (freq === "one-time") return "campaign";
        return freq || "campaign";
      };

      // Map milestones from API format to component format
      const mapMilestones = (apiMilestones) => {
        console.log("mapMilestones called with:", apiMilestones);
        if (!apiMilestones || !Array.isArray(apiMilestones) || apiMilestones.length === 0) {
          console.log("No milestones found, returning default");
          return [defaultMilestone()];
        }
        const seenServerIds = new Set();
        const mapped = [];
        apiMilestones.forEach((m, index) => {
          const serverMilestoneId = getApiMilestoneId(m);
          if (serverMilestoneId && seenServerIds.has(serverMilestoneId)) return;
          if (serverMilestoneId) seenServerIds.add(serverMilestoneId);

          const targetPercent =
            m.target_percentage !== undefined && m.target_percentage !== null
              ? String(m.target_percentage)
              : "";

          const mappedMilestone = {
            id: `m-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
            serverMilestoneId,
            name: m.milestone_name || "",
            targetPercent,
            rewardType:
              m.reward_type === "Trip"
                ? "Trip/Vacation"
                : m.reward_type === "Cash"
                  ? "Incentive"
                  : m.reward_type || "",
            rewardDetails: m.reward_details || "",
            enabled: m.is_active !== undefined ? Boolean(m.is_active) : true,
          };
          mapped.push(mappedMilestone);
        });
        console.log("All mapped milestones:", mapped);
        return mapped;
      };

      const mapEligibleUsers = (apiDataForEligibility) => {
        const rawEligibleType =
          apiDataForEligibility?.eligible_users ??
          apiDataForEligibility?.eligibleUsers ??
          "1";
        const rawPartnerCategory =
          apiDataForEligibility?.partner_category ??
          apiDataForEligibility?.partnerCategory ??
          [];
        const normalizedCategories = normalizeEligibleUsers(rawPartnerCategory);
        const mappedType =
          String(rawEligibleType) === "2" || normalizedCategories.length > 0
            ? "2"
            : "1";
        return {
          type: mappedType,
          categories: mappedType === "2" ? normalizedCategories : [],
        };
      };

      // Update form fields with API data
      // Handle both snake_case (from API) and camelCase (from listing) formats
      setStatus(apiData.status === "active" ? "published" : apiData.status || "draft");
      setContestName(apiData.contest_name || apiData.name || "");
      setContestNameError("");
      setSubtitle(apiData.subtitle || "");
      setDescription(apiData.description || "");
      setContestType(apiData.contest_type || apiData.contestType || "Trip");
      setCategory(apiData.category || "Sales & Performance");
      setStartDate(apiData.start_date || apiData.startDate || "");
      setEndDate(apiData.end_date || apiData.endDate || "");
      setFrequency(mapFrequency(apiData.frequency || apiData.type || "campaign"));
      setResetProgress(apiData.reset_progress === 1 || apiData.reset_progress === true || apiData.resetProgress === true);
      setProgressMetric(apiData.progress_metric || apiData.progressMetric || "Sales Value (Revenue)");
      setTargetValue(apiData.target_value?.toString() || apiData.targetValue?.toString() || "");
      setDisplayLabel(apiData.display_label || apiData.displayLabel || "Revenue to go");
      setMinThreshold(apiData.minimum_threshold?.toString() || apiData.minThreshold?.toString() || "");
      
      // Handle milestones - check both formats and ensure we have the array
      const milestonesData = apiData.milestones || apiData.milestone || [];
      console.log("Processing milestones - raw data:", milestonesData, "type:", typeof milestonesData, "isArray:", Array.isArray(milestonesData));
      console.log("Full apiData keys:", Object.keys(apiData));
      const mappedMilestones = mapMilestones(milestonesData);
      console.log("Setting milestones state with:", mappedMilestones, "count:", mappedMilestones.length);
      if (mappedMilestones.length > 0) {
        setMilestones(mappedMilestones);
      } else {
        console.warn("No milestones mapped, keeping existing state");
      }
      
      setEnableLeaderboard(mapEnableLeaderboardFromApi(apiData));
      setLeaderboardType(mapLeaderboardTypeFromApi(apiData.leaderboard_type ?? apiData.leaderboardType));
      const mappedEligibility = mapEligibleUsers(apiData);
      setEligibleUserType(mappedEligibility.type);
      setEligibleUsers(mappedEligibility.categories);
      
      // Banner: always reset file + preview to avoid stale image
      setBannerFile(null);
      setBannerError("");
      setBannerDragOver(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }

      // Set banner preview from API response (or clear)
      setBannerPreview(apiData.banner_image || apiData.imageUrl || null);
    } else {
      // If editContest has no banner info, ensure we don't show previous contest image
      setBannerFile(null);
      setBannerError("");
      setBannerDragOver(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }
      setBannerPreview(editContest?.banner_image || editContest?.imageUrl || null);
    }
  }, [editContest]);

  // Debug: Log milestones state changes
  useEffect(() => {
    console.log("Milestones state updated:", milestones);
  }, [milestones]);

  // Cleanup banner preview object URL on unmount or when banner changes
  useEffect(() => {
    return () => {
      if (bannerPreview && bannerPreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerPreview);
      }
    };
  }, [bannerPreview]);

  const addMilestone = () => {
    setMilestones((prev) => [...prev, defaultMilestone()]);
  };

  const removeMilestone = (id) => {
    setMilestones((prev) => prev.filter((m) => m.id !== id));
  };

  const updateMilestone = (id, field, value) => {
    setMilestones((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  };

  const handleMilestoneDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(milestones);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setMilestones(items);
  };

  const handleClearBanner = () => {
    // Revoke existing blob URL if any
    if (bannerPreview && bannerPreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(bannerPreview);
      } catch (e) {
        console.warn("Failed to revoke banner preview URL", e);
      }
    }
    setBannerFile(null);
    setBannerPreview(null);
    setBannerError("");
    if (bannerInputRef.current) {
      bannerInputRef.current.value = "";
    }
  };

  // Validate and set banner image with preview
  const handleBannerFile = (file) => {
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      setBannerError("Please select an image file");
      return;
    }

    // Clean up previous preview URL if it was a blob URL
    if (bannerPreview && bannerPreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreview);
    }

    // Validate image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      
      if (width !== 1024 || height !== 500) {
        setBannerError(`Image dimensions must be exactly 1024x500px. Current: ${width}x${height}px`);
        URL.revokeObjectURL(objectUrl);
        return;
      }
      
      // Valid dimensions - set file and preview
      setBannerError("");
      setBannerFile(file);
      setBannerPreview(objectUrl);
    };
    
    img.onerror = () => {
      setBannerError("Invalid image file");
      URL.revokeObjectURL(objectUrl);
    };
    
    img.src = objectUrl;
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleBannerFile(file);
  };

  const handleBannerDrop = (e) => {
    e.preventDefault();
    setBannerDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleBannerFile(file);
  };

  const handleSaveDraft = () => handleSubmit("draft");
  const handlePublish = () => handleSubmit("published");

  const toggleEligibleUser = (value) => {
    const normalizedValue = String(value);
    setEligibleUsers((prev) => {
      const current = Array.isArray(prev) ? prev : normalizeEligibleUsers(prev);

      if (current.includes(normalizedValue)) {
        const next = current.filter((v) => v !== normalizedValue);
        return next;
      }
      return [...current, normalizedValue];
    });
  };

  const toggleAllCategories = () => {
    setEligibleUsers((prev) => {
      const current = Array.isArray(prev) ? prev : normalizeEligibleUsers(prev);
      const categoryIds = categoryOptions.map((opt) => String(opt.value));
      const allSelected =
        categoryIds.length > 0 &&
        categoryIds.every((id) => current.includes(id));

      if (allSelected) {
        return [];
      }
      return categoryIds;
    });
  };

  const selectedEligibleUsersLabel = () => {
    if (eligibleUserType !== "2") {
      return "All Active Agents";
    }
    if (!eligibleUsers.length) {
      return "None selected";
    }
    const selectedLabels = eligibilityOptions
      .filter((opt) => eligibleUsers.includes(opt.value))
      .map((opt) => opt.label);
    return selectedLabels.length ? selectedLabels.join(", ") : "Select users";
  };

  // Read file as base64 data URL for banner
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Map form frequency to API value
  const getFrequencyApiValue = () => {
    if (frequency === "campaign") return "one-time";
    if (frequency === "monthly") return "monthly";
    if (frequency === "quarterly") return "quarterly";
    return "one-time";
  };

  // Build API payload for framework/api/CHLIRnR/create-contest
  const buildCreateContestPayload = (finalStatus, bannerObj = {}) => {
    const statusValue = finalStatus === "published" ? "active" : "draft";
    const existingBannerName =
      (editContest?.banner_image && String(editContest.banner_image).trim()) ||
      (typeof editContest?.imageUrl === "string" && editContest.imageUrl
        ? editContest.imageUrl.split("/").pop()?.split("?")[0] || ""
        : "");
    const bannerImageField = bannerFile?.name
      ? bannerFile.name
      : existingBannerName || "";

    const payload = {
      contest_name: contestName?.trim() || "",
      subtitle: subtitle?.trim() || "",
      description: description?.trim() || "",
      contest_type: contestType || "Trip",
      category: category || "Sales & Performance",
      banner_image: bannerImageField,
      start_date: startDate || "",
      end_date: endDate || "",
      frequency: getFrequencyApiValue(),
      reset_progress: Boolean(resetProgress),
      progress_metric: progressMetric || "Sales Value",
      target_value: Number(targetValue) || 0,
      display_label: displayLabel?.trim() || "Revenue to go",
      minimum_threshold: Number(minThreshold) || 0,
      status: statusValue,
      is_leaderboard: enableLeaderboard ? 1 : 0,
      leaderboard_type: leaderboardType || "1",
      eligible_users: eligibleUserType,
      partner_category:
        eligibleUserType === "2"
          ? eligibleUsers.map((value) => toApiPartnerCategory(value))
          : [],
      milestones: (milestones || []).map((m, index) => {
        const rewardType = m.rewardType || "";
        const apiRewardType =
          rewardType === "Trip/Vacation"
            ? "Trip"
            : rewardType === "Incentive"
              ? "Cash"
              : rewardType || "Cash";
        const row = {
          milestone_name: m.name?.trim() || "",
          target_percentage: Number(m.targetPercent) || 0,
          reward_type: apiRewardType,
          reward_details: m.rewardDetails?.trim() || "",
          is_active: Boolean(m.enabled),
          // Order after drag-and-drop (1 = first row, top to bottom)
          position: index + 1,
        };
        if (isEdit && m.serverMilestoneId) {
          row.id = String(m.serverMilestoneId);
        }
        return row;
      }),
      banner: bannerObj,
    };
    
    // Include contest_id when editing
    if (isEdit && editContest?.id) {
      payload.contest_id = String(editContest.id);
    }
    
    return payload;
  };

  const handleSubmit = async (finalStatus) => {
    if (finalStatus === "published" && !contestName?.trim()) {
      setContestNameError("Contest name is required to publish.");
      contestNameSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setContestNameError("");

    const hasBanner =
      Boolean(bannerFile?.name) ||
      Boolean(bannerPreview && String(bannerPreview).trim() !== "");
    if (!hasBanner) {
      setBannerError("Banner image is required. Upload a 1024×500px image.");
      bannerSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setBannerError("");

    let bannerObj = {};
    if (bannerFile && bannerFile.name) {
      try {
        const base64 = await fileToBase64(bannerFile);
        bannerObj = {
          base64,
          file: {
            name: bannerFile.name,
            size: bannerFile.size,
            type: bannerFile.type || "image/jpeg",
          },
        };
      } catch (e) {
        console.error("Banner read error", e);
      }
    }
    const apiPayload = buildCreateContestPayload(finalStatus, bannerObj);
    onSave?.(apiPayload);
  };

  const formTitle = isEdit ? "Edit Contest" : "Create New Contest";

  const footerButtons = (
    <>
      <Button variant="outline-secondary" className="btn-footer-cancel" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="outline-success" className="btn-footer-draft" onClick={handleSaveDraft}>
        Save as Draft
      </Button>
      <Button variant="primary" className="btn-footer-publish" onClick={handlePublish}>
        Publish Contest
      </Button>
    </>
  );

  const formContent = (
    <>
      {/* 1. Header – title left, Status: Draft box right (matches design) */}
      <div className="create-contest-header-section">
        <div className="create-contest-page-header">
          <h1 className="create-contest-page-title">{formTitle}</h1>
          <div className="create-contest-status-box">
            <Button variant="outline-secondary" className="btn-footer-cancel" onClick={onClose}>
              <ChevronLeft size={12} className="me-2" />
              Back to Contests
            </Button>
          </div>
        </div>
      </div>
      <div className="create-contest-form-body-inner">
      {/* 2. Basic Contest Details – design: two-column top, description full, two-column type/category, banner upload */}
      <div className="create-contest-section create-contest-basic-details">
        <div className="section-title">Basic Contest Details</div>
        <div className="section-subtitle">This information appears on the contest card in the mobile app.</div>

        <Row>
          <Col md={6}>
            <div ref={contestNameSectionRef}>
            <Form.Group className="mb-2">
              <Form.Label>Contest Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={contestName}
                onChange={(e) => {
                  setContestName(e.target.value);
                  if (contestNameError) setContestNameError("");
                }}
                placeholder=""
                isInvalid={Boolean(contestNameError)}
              />
              <Form.Control.Feedback type="invalid">{contestNameError}</Form.Control.Feedback>
            </Form.Group>
            </div>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle (Optional)</Form.Label>
              <Form.Control
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="e.g. 80 days left"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Contest rules or motivation"
          />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Contest Type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={contestType}
                onChange={(e) => setContestType(e.target.value)}
              >
                {CONTEST_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-0" ref={bannerSectionRef}>
          <Form.Label>Banner Image <span className="text-danger">*</span></Form.Label>
          {bannerPreview && (
            <div className="banner-preview-container mb-3">
              <div className="banner-preview-inner">
                <button
                  type="button"
                  className="banner-preview-remove-btn"
                  onClick={handleClearBanner}
                  aria-label="Remove banner image"
                >
                  <XCircle size={18} />
                </button>
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  className="banner-preview-image"
                />
              </div>
              {bannerFile && (
                <div className="mt-2">
                  <span className="text-muted small">{bannerFile.name}</span>
                </div>
              )}
            </div>
          )}
          {!bannerPreview && (
            <div
              className={`banner-upload-zone ${bannerDragOver ? "banner-upload-zone--dragover" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setBannerDragOver(true);
              }}
              onDragLeave={() => setBannerDragOver(false)}
              onDrop={handleBannerDrop}
              onClick={() => bannerInputRef.current?.click()}
            >
              <CloudArrowUp className="banner-upload-icon" size={40} />
              {bannerFile ? (
                <>
                  <span className="banner-upload-text">Change image</span>
                  <span className="banner-upload-filename">{bannerFile.name}</span>
                </>
              ) : (
                <>
                  <span className="banner-upload-text">Click to upload or drag and drop</span>
                  <span className="banner-upload-hint">SVG, PNG, JPG or GIF (1024x500px)</span>
                </>
              )}
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/gif"
                className="d-none"
                onChange={handleBannerFileChange}
              />
            </div>
          )}
          {bannerError && (
            <div className="text-danger small mt-2">{bannerError}</div>
          )}
        </Form.Group>
      </div>

      {/* 3. Duration & Frequency – design: Start/End dates top row, Frequency | Reset Progress bottom row */}
      <div className="create-contest-section create-contest-duration-frequency">
        <div className="section-title">Duration & Frequency</div>
        <div className="section-subtitle">Set when the contest starts, ends, and how often it repeats.</div>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <InputGroup.Text className="duration-date-icon">
                  <Calendar3 size={18} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  className="duration-date-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <InputGroup.Text className="duration-date-icon">
                  <Calendar3 size={18} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  className="duration-date-input"
                  min={endDateMin}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Frequency <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <div className="reset-progress-box">
              <div className="reset-progress-label-wrap">
                <div className="reset-progress-label">Reset Progress</div>
                <p className="reset-progress-desc">Reset user progress each cycle?</p>
              </div>
              <div className="reset-progress-toggle">
                <Form.Check
                  type="switch"
                  checked={resetProgress}
                  onChange={(e) => setResetProgress(e.target.checked)}
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* 4. Progress & Scoring Logic – design: 2x2 grid */}
      <div className="create-contest-section create-contest-progress-scoring">
        <div className="section-title">Progress & Scoring Logic</div>
        <div className="section-subtitle">Define how progress is measured and displayed.</div>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Progress Metric <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={progressMetric}
                onChange={(e) => setProgressMetric(e.target.value)}
              >
                {PROGRESS_METRICS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Target Value <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="10,000,000"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Display Label (e.g. &quot;Points Left&quot;)</Form.Label>
              <Form.Control
                value={displayLabel}
                onChange={(e) => setDisplayLabel(e.target.value)}
                placeholder="Revenue to go"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Minimum Threshold</Form.Label>
              <Form.Control
                type="number"
                value={minThreshold}
                onChange={(e) => setMinThreshold(e.target.value)}
                placeholder="500,000"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* 5. Milestones & Rewards – draggable table */}
      <div className="create-contest-section create-contest-milestones">
        <div className="milestones-section-header">
          <div>
            <div className="section-title">5. Milestones & Rewards</div>
            <div className="section-subtitle">Define reward tiers. Drag to reorder.</div>
          </div>
          <Button variant="outline-success" className="btn-add-milestone" onClick={addMilestone}>
            <Plus className="me-1" size={16} />
            Add Milestone
          </Button>
        </div>
        <div className="milestones-table-wrap">
          <DragDropContext onDragEnd={handleMilestoneDragEnd}>
            <table className="milestones-table">
              <thead>
                <tr>
                  <th className="milestone-col-drag"></th>
                  <th className="milestone-col-name">Milestone Name</th>
                  <th className="milestone-col-target">Target %</th>
                  <th className="milestone-col-reward">Reward Type</th>
                  <th className="milestone-col-details">Reward Details</th>
                  <th className="milestone-col-status">Status</th>
                  <th className="milestone-col-actions"></th>
                </tr>
              </thead>
              <Droppable droppableId="milestones-droppable">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {milestones.map((m, index) => (
                      <Draggable key={m.id} draggableId={m.id} index={index}>
                        {(provided) => (
                          <tr
                            className="milestone-row"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <td className="milestone-col-drag" {...provided.dragHandleProps}>
                              <span className="milestone-drag-handle" aria-label="Drag to reorder">
                                <GripVertical size={18} />
                              </span>
                            </td>
                            <td className="milestone-col-name">
                              <Form.Control
                                className="milestone-input"
                                placeholder="e.g. Level 1"
                                value={m.name}
                                onChange={(e) => updateMilestone(m.id, "name", e.target.value)}
                              />
                            </td>
                            <td className="milestone-col-target">
                              <Form.Control
                                className="milestone-input"
                                placeholder="50"
                                value={m.targetPercent}
                                onChange={(e) => updateMilestone(m.id, "targetPercent", e.target.value)}
                              />
                            </td>
                            <td className="milestone-col-reward">
                              <Form.Select
                                className="milestone-input"
                                value={m.rewardType}
                                onChange={(e) => updateMilestone(m.id, "rewardType", e.target.value)}
                              >
                                <option value="">Select</option>
                                {REWARD_TYPES.map((r) => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </Form.Select>
                            </td>
                            <td className="milestone-col-details">
                              <Form.Control
                                className="milestone-input"
                                placeholder="e.g. ₹10,000"
                                value={m.rewardDetails}
                                onChange={(e) => updateMilestone(m.id, "rewardDetails", e.target.value)}
                              />
                            </td>
                            <td className="milestone-col-status">
                              <Form.Check
                                type="switch"
                                className="milestone-status-toggle"
                                checked={m.enabled}
                                onChange={(e) => updateMilestone(m.id, "enabled", e.target.checked)}
                                title="Enable"
                              />
                            </td>
                            <td className="milestone-col-actions">
                              <Button
                                variant="link"
                                size="sm"
                                className="btn-remove-milestone p-0"
                                onClick={() => removeMilestone(m.id)}
                                title="Remove"
                              >
                                <Trash size={16} />
                              </Button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
        </div>
      </div>

      {/* Leaderboard & Eligibility – side-by-side cards (design) */}
      <Row>
        <Col md={6}>
          <div className="create-contest-section create-contest-card create-contest-leaderboard">
            <div className="section-title">Leaderboard</div>
            <div className="leaderboard-enable-box">
              <Form.Label className="leaderboard-toggle-label mb-0">Enable Leaderboard</Form.Label>
              <Form.Check
                type="switch"
                className="leaderboard-toggle"
                checked={enableLeaderboard}
                onChange={(e) => setEnableLeaderboard(e.target.checked)}
              />
            </div>
            <Form.Group className="mb-0 mt-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={leaderboardType}
                onChange={(e) => setLeaderboardType(e.target.value)}
                className="leaderboard-type-select"
              >
                {LEADERBOARD_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </Col>
        <Col md={6}>
          <div className="create-contest-section create-contest-card create-contest-eligibility">
            <div className="section-title">Eligibility</div>
            <Form.Group className="mb-0">
              <Form.Label>Eligible Users <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={eligibleUserType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setEligibleUserType(nextType);
                  if (nextType !== "2") {
                    setEligibleUsers([]);
                    setEligibleUsersSearch("");
                  }
                }}
                className="mb-2"
              >
                {ELIGIBLE_USER_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
              {eligibleUserType === "2" && (
                <div className="border rounded p-2 bg-white">
                  <Form.Control
                    type="text"
                    value={eligibleUsersSearch}
                    onChange={(e) => setEligibleUsersSearch(e.target.value)}
                    placeholder="Search partner category..."
                    className="mb-2"
                  />
                  <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                    <Form.Check
                      id="eligible-user-all-categories"
                      type="checkbox"
                      className="mb-1"
                      label="All"
                      checked={areAllCategoriesSelected}
                      onChange={toggleAllCategories}
                      disabled={categoryOptions.length === 0}
                    />
                    {filteredEligibilityOptions.map((o) => (
                      <Form.Check
                        key={o.value}
                        id={`eligible-user-${o.value}`}
                        type="checkbox"
                        className="mb-1"
                        label={o.label}
                        checked={eligibleUsers.includes(String(o.value))}
                        onChange={() => toggleEligibleUser(String(o.value))}
                      />
                    ))}
                    {filteredEligibilityOptions.length === 0 && (
                      <div className="text-muted small">No matching category found</div>
                    )}
                  </div>
                </div>
              )}
              <div className="text-muted small mt-2">
                Selected: {selectedEligibleUsersLabel()}
              </div>
            </Form.Group>
          </div>
        </Col>
        </Row>
        </div>
    </>
  );

  if (asPage) {
    return (
      <div className="create-contest-page-content">
        {/* <Button
          variant="link"
          className="create-contest-back-btn p-0 text-decoration-none mb-3"
          onClick={onClose}
        >
          <ArrowLeft size={18} className="me-1" />
          Back to Contests
        </Button> */}
        <div className="create-contest-form-body">
          {formContent}
        </div>
        <div className="create-contest-page-footer">
          {footerButtons}
        </div>
      </div>
    );
  }

  return (
    <Modal show size="xl" onHide={onClose} scrollable>
      <Modal.Header closeButton />
      <Modal.Body>
        {formContent}
      </Modal.Body>
      <Modal.Footer>
        {footerButtons}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateContestModal;
